/**
 * One theme copy for the client and the server (the onboarding theme switch,
 * `modules/theme-v2/plans/2026-10-05-onboarding-theme-switch-plan.md` D1).
 *
 * The UI and the CLI both switch an onboarding's theme through one builder route, and that route copies
 * with THESE functions, which call the very conversions the create wizard and the strategy Replace Theme
 * use. So "full theme" means exactly what the client conversions produce, and the two can never drift.
 *
 * Value imports are RELATIVE: builder-service imports this file, and its runtime has no `@modules/*`
 * resolution (CLAUDE.md, Guard 4; `node scripts/check-builder-module-leaks.mjs`).
 */
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';
import { DEFAULT_ACTION_BAR, DEFAULT_BACKDROP, DEFAULT_HEADER_BAR } from '../types/themesV2.types';
import type { ThemeV2 } from '@modules/themes/types/themesV2.types';
import {
  cloneOnboardingThemeSettings,
  onboardingSettingsV2ToThemeV2,
  themeV2ToOnboardingSettings,
} from './themeV2EditorTransforms';

/** Where a theme comes from: the project's own list or the system list (`themesV2`). */
export type OnboardingThemeSourceKind = 'project' | 'system';

/** Fields a stored theme owns; a copy never carries the source's values of them. */
export const THEME_COPY_LIFECYCLE_FIELDS = [
  'id',
  'createdAt',
  'updatedAt',
  'isActive',
  'order',
  'sourceThemeId',
] as const;

/** A system theme document body: a full theme without its id and timestamps. */
export type SystemThemeBody = Omit<ThemeV2, 'id' | 'createdAt' | 'updatedAt'>;

/**
 * Copies a stored theme into onboarding theme settings, the way the client does for that source kind:
 * `cloneOnboardingThemeSettings` for a project theme, `themeV2ToOnboardingSettings` for a system theme,
 * both with the wizard's bar and background defaults. The settings name `themeId` — the picked theme — as
 * their source, never the source's own `sourceThemeId`.
 */
export function copyThemeToOnboardingSettings(
  source: Record<string, unknown>,
  kind: OnboardingThemeSourceKind,
  themeId: string,
): OnboardingThemeSettingsV2 {
  // The wizard's defaults for a theme stored without its bars or background (both kinds).
  const withBars = {
    ...source,
    background: (source.background as ThemeV2['background']) ?? DEFAULT_BACKDROP,
    headerBar: (source.headerBar as ThemeV2['headerBar']) ?? DEFAULT_HEADER_BAR,
    actionBar: (source.actionBar as ThemeV2['actionBar']) ?? DEFAULT_ACTION_BAR,
  };
  const settings = kind === 'project'
    ? cloneOnboardingThemeSettings(withBars as unknown as OnboardingThemeSettingsV2, { sourceThemeId: themeId })
    : themeV2ToOnboardingSettings(withBars as unknown as ThemeV2, { sourceThemeId: themeId });
  return stripUndefinedDeep(settings) as OnboardingThemeSettingsV2;
}

/**
 * Turns onboarding theme settings (or a project theme, which has the same shape) into a system theme
 * body with `onboardingSettingsV2ToThemeV2`, the conversion the client uses for "onboarding → theme".
 * The source's lifecycle fields are dropped; `name` is the given one and `order` is stored only when given.
 */
export function onboardingThemeToSystemTheme(
  source: Record<string, unknown>,
  options: { name: string; order?: number },
): SystemThemeBody {
  const theme = onboardingSettingsV2ToThemeV2(source as unknown as OnboardingThemeSettingsV2, {
    name: options.name,
  }) as unknown as Record<string, unknown>;
  for (const field of THEME_COPY_LIFECYCLE_FIELDS) {
    delete theme[field];
  }
  theme.name = options.name;
  if (options.order !== undefined) {
    theme.order = options.order;
  }
  return stripUndefinedDeep(theme) as SystemThemeBody;
}

/** Firestore rejects `undefined` at any depth; `null` is a value and stays. */
function stripUndefinedDeep(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(stripUndefinedDeep);
  }
  if (!value || typeof value !== 'object') {
    return value;
  }
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter(([, entry]) => entry !== undefined)
      .map(([key, entry]) => [key, stripUndefinedDeep(entry)]),
  );
}
